import {
  Button,
  DropdownButton,
  Image,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import { accessControl } from 'lib-shared-access-control';
import {
  AsyncStatus,
  EmailDraftT,
  EmailIntegration,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { Component } from 'react';
import { Link } from 'react-router-dom';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';

import ComposeEmailHelper from '../../../shared/composeEmail/helper/ComposeEmailHelper';
import { TopbarButton } from '../topbarButton/';

import styles from './emailTopbarButton.module.scss';
import ComposeEmailNoDraftImage from './no-conversations.png';

type PropsT = {
  composeEmailOpenModal: (draft?: { initialValues: EmailDraftT }) => void;
  composeEmailGetDrafts: () => Promise<any>;
  composeEmailRemoveDraft: (payload: { timestamp: number }) => Promise<any>;
  composeEmailGetAllFrom: () => Promise<any>;
  fromEmails: Array<EmailIntegration>;
  emailDrafts: Array<EmailDraftT>;
  meta: {
    getAllFrom: AsyncStatus;
    getDrafts: AsyncStatus;
    removeDrafts: AsyncStatus;
  };
  className?: string;
  alignCenter?: boolean;
};

export class EmailTopbarButton extends Component<PropsT> {
  componentDidMount() {
    this.props.composeEmailGetDrafts();
    this.props.composeEmailGetAllFrom();
  }

  @boundMethod
  onDropdownToggle(isOpen: boolean) {
    if (isOpen === true) {
      this.props.composeEmailGetDrafts();
      ampli.topbarEmailOpened();
    }
  }

  @boundMethod
  openCompose(draft?: { initialValues: EmailDraftT }) {
    ComposeEmailHelper.focus();
    this.props.composeEmailOpenModal(draft);
  }

  render() {
    const { className } = this.props;
    let draftRender;
    let showNotificationIndicator = false;
    let noEmailIntegration;
    let restOftotalDraftsNumber = 0;
    let moreLayout;
    const hasIntegrationSetupAccess = accessControl.hasAccess(
      'settings.integration.channel.setup',
      {
        fallbackRole: 'admin',
      },
    );

    const rootClass = classnames(
      {
        [styles.root]: styles.root,
      },
      className,
    );

    if (
      this.props.fromEmails.length === 0 &&
      this.props.meta.getAllFrom === 'succeeded'
    ) {
      noEmailIntegration = (
        <li className={styles.empty + ' text-center'}>
          No email integration found{' '}
          {hasIntegrationSetupAccess && (
            <Link className="btn btn-link btn-xs " to="/settings/email">
              Create one in email settings
            </Link>
          )}
        </li>
      );
    }

    const listDraft = (draft: EmailDraftT, index: number) => {
      return (
        <li key={index} className={styles.item}>
          <div className={styles.draftItemHolder + ' pull-left'}>
            <h4 className={styles.title}>
              <em>
                {draft.subject !== undefined ? draft.subject : 'unknown'}{' '}
                (Unsent draft)
              </em>
            </h4>
            <h6 className={styles.time}>{moment(draft.timestamp).fromNow()}</h6>
          </div>
          <div className={styles.draftActionsHolder}>
            <button
              type="button"
              className={styles.view + ' btn'}
              onClick={() => {
                this.openCompose({ initialValues: draft });
              }}
            >
              View
            </button>
            <button
              type="button"
              className={styles.remove + ' btn'}
              onClick={() =>
                this.props.composeEmailRemoveDraft({
                  timestamp: draft.timestamp,
                })
              }
            >
              Remove
            </button>
          </div>
        </li>
      );
    };

    if (this.props.emailDrafts.length > 0) {
      showNotificationIndicator = true;
      const draftsArray = [...this.props.emailDrafts];
      if (draftsArray.length > 5) {
        draftRender = draftsArray.slice(0, 5).map(listDraft);
      } else {
        draftRender = draftsArray.map(listDraft);
      }
    } else if (
      this.props.emailDrafts.length === 0 &&
      (this.props.meta.getDrafts === 'loading' ||
        this.props.meta.removeDrafts === 'loading')
    ) {
      draftRender = <li className={styles.empty}>loading...</li>;
    } else if (this.props.emailDrafts.length === 0) {
      draftRender = (
        <div className={styles.empty}>
          <Image
            src={ComposeEmailNoDraftImage}
            className={styles.emptyImage}
            alt="No draft email"
          />
          <h3 className={styles.heading}>No drafts here</h3>
        </div>
      );
    }

    if (this.props.emailDrafts.length > 5) {
      restOftotalDraftsNumber = this.props.emailDrafts.length - 5;
    }

    if (this.props.emailDrafts.length > 0) {
      moreLayout = (
        <li role="more" className={styles.more}>
          <Link to="/drafts">
            See all drafts{' '}
            {restOftotalDraftsNumber > 0
              ? `(${restOftotalDraftsNumber} more)`
              : this.props.emailDrafts.length > 0
              ? `(${this.props.emailDrafts.length})`
              : ''}
          </Link>
        </li>
      );
    }

    const tooltip = (
      <CommandKeybindingsTooltipContent commandName="conversation:composeNewEmail">
        New e-mail
      </CommandKeybindingsTooltipContent>
    );

    const DropdownContent = (
      <ul
        className={classnames({
          [styles.content]: true,
          [styles.fullWidthContent]: this.props.alignCenter,
        })}
      >
        <li role="headline" className={styles.headline}>
          <Text.Heading>Email</Text.Heading>
        </li>
        <li role="separator" className={styles.divider}></li>
        {draftRender}
        {noEmailIntegration}
        <li role="more" className={styles.more}>
          <Tooltip content={tooltip} dark>
            <Button
              type="button"
              variant="secondary"
              style={{ width: '100%' }}
              onClick={() => this.openCompose()}
            >
              Compose
            </Button>
          </Tooltip>
        </li>
        <li role="separator" className={styles.divider}></li>
        {moreLayout}
      </ul>
    );

    return (
      <DropdownButton
        icon="mail"
        className={rootClass}
        onToggle={this.onDropdownToggle}
        noArrow
        noStyle
        dropdownProps={{
          closeOnBlur: false,
          className: classnames({
            [styles.dropdownMenuAlignCenter]: this.props.alignCenter,
          }),
        }}
        dropdown={DropdownContent}
        data-testid="compose-email-dropdown"
      >
        <TopbarButton
          icon="mail"
          tooltip={tooltip}
          notificationIndicatorTooltip={
            showNotificationIndicator
              ? 'You have unused email drafts'
              : undefined
          }
          notificationIndicatorClassName={styles.notificationIndicator}
          showNotificationIndicator={showNotificationIndicator}
        />
      </DropdownButton>
    );
  }
}
